import type { ConvexClient } from 'convex/browser';

export const SESSION_TOKEN_STORAGE_KEY = 'convex-rebuild.token';
export const CONVEX_SESSION_STORAGE_KEY = 'convex-rebuild.convex-session';

const CONVEX_SESSION_REFRESH_LEEWAY_MS = 60_000;
const RECORDER_MODE_HASH_FLAG = 'config.iAmRecorder=true';
const RECORDER_XMPP_USERNAME_OVERRIDE_STORAGE_KEY = 'xmpp_username_override';
const RECORDER_XMPP_PASSWORD_OVERRIDE_STORAGE_KEY = 'xmpp_password_override';
const NON_CONFERENCE_PATH_SEGMENTS = new Set(['byebye', 'invalidsession', 'kicked', 'unsupported-browser']);

type BrowserEnvironment = {
  fetch: typeof window.fetch;
  history: Pick<History, 'replaceState'>;
  localStorage: Pick<Storage, 'getItem'>;
  location: Pick<Location, 'hash' | 'href' | 'pathname'>;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
};

type StoredConvexSession = {
  expiresAt: number;
  inviteTokenHash: string;
  token: string;
};

export type InviteRole = 'student' | 'teacher' | 'recorder';

export type TokenSessionState = {
  replacedStoredToken: boolean;
  scrubbedTokenFromUrl: boolean;
  token: string | null;
};

export type InviteTokenFailureReason = 'expired' | 'invalid';

export type InviteAuthBootstrapFailure = {
  message: string;
  reason: InviteTokenFailureReason;
  status: number;
};

type InviteTokenExchangeResponse = {
  applicationId: string;
  expiresAt: number;
  identity: {
    displayName: string;
    inviteTokenHash: string;
    role: InviteRole;
    source: 'invite-jwt';
    subject: string;
  };
  issuer: string;
  token: string;
  tokenType: 'Bearer';
};

export type RecorderBootstrapTokenResponse = {
  conferenceSlug: string;
  expiresAt: number;
  identity: {
    displayName: string;
    role: 'recorder';
    subject: string;
  };
  token: string;
  tokenType: 'Bearer';
};

export type RecorderXmppCredentials = {
  xmppJid: string;
  xmppPassword: string;
};

export type RecorderBootstrapState = {
  attempted: boolean;
  error: string | null;
  token: string | null;
};

type AuthDebugState = {
  authenticated: boolean;
  exchangeCount: number;
  inviteTokenSeen: boolean;
  lastError: string | null;
  lastInviteTokenHash: string | null;
  lastSessionExpiresAt: number | null;
  lastSessionIssuer: string | null;
  lastSessionSubject: string | null;
  sessionTokenReady: boolean;
};

type EnsureConvexAuthBootstrapOptions = {
  onInviteTokenFailure?: (failure: InviteAuthBootstrapFailure) => void;
};

declare global {
  interface Window {
    __convexRebuildAuthDebug?: AuthDebugState;
  }
}

const bootstrappedClients = new WeakSet<ConvexClient>();
const authDebugEnabled =
  import.meta.env.DEV || import.meta.env.MODE === 'test' || import.meta.env.VITE_ENABLE_AUTH_DEBUG === 'true';
const EXPIRED_INVITE_TOKEN_ERROR_MESSAGE = 'Invite token has expired.';

class InviteTokenExchangeFailure extends Error {
  reason: InviteTokenFailureReason;
  status: number;

  constructor(reason: InviteTokenFailureReason, message: string, status: number) {
    super(message);
    this.name = 'InviteTokenExchangeFailure';
    this.reason = reason;
    this.status = status;
  }
}

function createEmptyAuthDebugState(): AuthDebugState {
  return {
    authenticated: false,
    exchangeCount: 0,
    inviteTokenSeen: false,
    lastError: null,
    lastInviteTokenHash: null,
    lastSessionExpiresAt: null,
    lastSessionIssuer: null,
    lastSessionSubject: null,
    sessionTokenReady: false,
  };
}

function getDefaultEnvironment(): BrowserEnvironment {
  return {
    fetch: window.fetch.bind(window),
    history: window.history,
    localStorage: window.localStorage,
    location: window.location,
    storage: window.sessionStorage,
  };
}

function getAuthDebugState(): AuthDebugState {
  return window.__convexRebuildAuthDebug ?? createEmptyAuthDebugState();
}

function updateAuthDebugState(patch: Partial<AuthDebugState>): void {
  if (!authDebugEnabled) {
    return;
  }

  window.__convexRebuildAuthDebug = {
    ...getAuthDebugState(),
    ...patch,
  };
}

function deriveConvexSiteUrl(convexUrl: string): string {
  const parsedUrl = new URL(convexUrl);

  if (!parsedUrl.hostname.includes('.convex.cloud')) {
    throw new Error(`Unable to derive a Convex site URL from ${convexUrl}. Set VITE_CONVEX_SITE_URL explicitly.`);
  }

  parsedUrl.hostname = parsedUrl.hostname.replace('.convex.cloud', '.convex.site');
  parsedUrl.pathname = '';
  parsedUrl.search = '';
  parsedUrl.hash = '';

  return parsedUrl.toString().replace(/\/$/, '');
}

export function getConvexSiteUrl(): string {
  const configuredSiteUrl = import.meta.env.VITE_CONVEX_SITE_URL;

  if (configuredSiteUrl) {
    return configuredSiteUrl.replace(/\/$/, '');
  }

  const convexUrl = import.meta.env.VITE_CONVEX_URL;

  if (!convexUrl) {
    throw new Error('Missing VITE_CONVEX_URL for Convex auth bootstrap.');
  }

  return deriveConvexSiteUrl(convexUrl);
}

function readRouteSegments(pathname: string): string[] {
  const appBasePath = new URL(import.meta.env.BASE_URL, 'https://conference.invalid').pathname;
  const baseSegments = appBasePath.split('/').filter(Boolean);
  const pathSegments = pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));

  if (baseSegments.length === 0) {
    return pathSegments;
  }

  let matchedBaseSegments = 0;

  while (
    matchedBaseSegments < baseSegments.length &&
    matchedBaseSegments < pathSegments.length &&
    pathSegments[matchedBaseSegments] === baseSegments[matchedBaseSegments]
  ) {
    matchedBaseSegments += 1;
  }

  return pathSegments.slice(matchedBaseSegments);
}

function readConferenceSlugFromLocation(location: Pick<Location, 'pathname'>): string | null {
  const [firstSegment] = readRouteSegments(location.pathname);

  if (!firstSegment || NON_CONFERENCE_PATH_SEGMENTS.has(firstSegment)) {
    return null;
  }

  return firstSegment;
}

export function readRecorderXmppCredentials(
  storage: Pick<Storage, 'getItem'> = getDefaultEnvironment().localStorage,
): RecorderXmppCredentials | null {
  const xmppJid = storage.getItem(RECORDER_XMPP_USERNAME_OVERRIDE_STORAGE_KEY)?.trim();
  const xmppPassword = storage.getItem(RECORDER_XMPP_PASSWORD_OVERRIDE_STORAGE_KEY);

  if (!xmppJid || !xmppPassword) {
    return null;
  }

  return {
    xmppJid,
    xmppPassword,
  };
}

export function isRecorderMode(environment: BrowserEnvironment = getDefaultEnvironment()): boolean {
  return (
    environment.location.hash.includes(RECORDER_MODE_HASH_FLAG) && readRecorderXmppCredentials(environment.localStorage) !== null
  );
}

export function readStoredToken(storage: Pick<Storage, 'getItem'> = getDefaultEnvironment().storage): string | null {
  return storage.getItem(SESSION_TOKEN_STORAGE_KEY);
}

function clearStoredToken(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(SESSION_TOKEN_STORAGE_KEY);
}

function readStoredConvexSession(
  storage: Pick<Storage, 'getItem'> = getDefaultEnvironment().storage,
): StoredConvexSession | null {
  const value = storage.getItem(CONVEX_SESSION_STORAGE_KEY);

  if (!value) {
    return null;
  }

  return JSON.parse(value) as StoredConvexSession;
}

function writeStoredConvexSession(storage: Pick<Storage, 'setItem'>, session: StoredConvexSession): void {
  storage.setItem(CONVEX_SESSION_STORAGE_KEY, JSON.stringify(session));
}

function clearStoredConvexSession(storage: Pick<Storage, 'removeItem'>): void {
  storage.removeItem(CONVEX_SESSION_STORAGE_KEY);
}

async function hashToken(token: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  const bytes = Array.from(new Uint8Array(digest));
  return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function exchangeInviteToken(
  inviteToken: string,
  environment: Pick<BrowserEnvironment, 'fetch'>,
): Promise<InviteTokenExchangeResponse> {
  const response = await environment.fetch(`${getConvexSiteUrl()}/auth/exchange-invite`, {
    body: new URLSearchParams({ token: inviteToken }),
    headers: {
      Accept: 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    let message = `Convex auth bootstrap failed with ${response.status} ${response.statusText}.`;

    try {
      const body = (await response.json()) as { error?: string };

      if (typeof body.error === 'string' && body.error.length > 0) {
        message = body.error;
      }
    } catch {
      // Ignore JSON parse failures and fall back to the HTTP status text.
    }

    if (response.status === 401) {
      throw new InviteTokenExchangeFailure(
        message === EXPIRED_INVITE_TOKEN_ERROR_MESSAGE ? 'expired' : 'invalid',
        message,
        response.status,
      );
    }

    throw new Error(message);
  }

  return (await response.json()) as InviteTokenExchangeResponse;
}

async function bootstrapRecorderInviteToken(
  conferenceSlug: string,
  credentials: RecorderXmppCredentials,
  environment: Pick<BrowserEnvironment, 'fetch'>,
): Promise<RecorderBootstrapTokenResponse> {
  const response = await environment.fetch(`${getConvexSiteUrl()}/auth/bootstrap-recorder`, {
    body: new URLSearchParams({
      conferenceSlug,
      xmppJid: credentials.xmppJid,
      xmppPassword: credentials.xmppPassword,
    }),
    headers: {
      Accept: 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    let message = `Recorder bootstrap failed with ${response.status} ${response.statusText}.`;

    try {
      const body = (await response.json()) as { error?: string };

      if (typeof body.error === 'string' && body.error.length > 0) {
        message = body.error;
      }
    } catch {
      // Ignore JSON parse failures and fall back to the HTTP status text.
    }

    throw new Error(message);
  }

  return (await response.json()) as RecorderBootstrapTokenResponse;
}

export async function initializeRecorderBootstrapSession(
  environment: BrowserEnvironment = getDefaultEnvironment(),
): Promise<RecorderBootstrapState> {
  const currentUrl = new URL(environment.location.href);

  if (currentUrl.searchParams.get('token') || !isRecorderMode(environment)) {
    return {
      attempted: false,
      error: null,
      token: null,
    };
  }

  const conferenceSlug = readConferenceSlugFromLocation(environment.location);
  const credentials = readRecorderXmppCredentials(environment.localStorage);

  if (!conferenceSlug || !credentials) {
    return {
      attempted: false,
      error: null,
      token: null,
    };
  }

  try {
    const result = await bootstrapRecorderInviteToken(conferenceSlug, credentials, environment);
    const previousToken = environment.storage.getItem(SESSION_TOKEN_STORAGE_KEY);

    environment.storage.setItem(SESSION_TOKEN_STORAGE_KEY, result.token);

    if (previousToken !== result.token) {
      clearStoredConvexSession(environment.storage);
    }

    updateAuthDebugState({
      inviteTokenSeen: true,
      lastError: null,
      lastInviteTokenHash: await hashToken(result.token),
      sessionTokenReady: false,
    });

    return {
      attempted: true,
      error: null,
      token: result.token,
    };
  } catch (error) {
    environment.storage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    clearStoredConvexSession(environment.storage);
    updateAuthDebugState({
      authenticated: false,
      inviteTokenSeen: false,
      lastError: error instanceof Error ? error.message : 'Unknown recorder bootstrap error.',
      lastInviteTokenHash: null,
      lastSessionExpiresAt: null,
      lastSessionIssuer: null,
      lastSessionSubject: null,
      sessionTokenReady: false,
    });

    return {
      attempted: true,
      error: error instanceof Error ? error.message : 'Unknown recorder bootstrap error.',
      token: null,
    };
  }
}

export function initializeAuthTokenSession(environment: BrowserEnvironment = getDefaultEnvironment()): TokenSessionState {
  const currentUrl = new URL(environment.location.href);
  const previousToken = environment.storage.getItem(SESSION_TOKEN_STORAGE_KEY);
  const urlToken = currentUrl.searchParams.get('token');

  if (urlToken === null) {
    return {
      replacedStoredToken: false,
      scrubbedTokenFromUrl: false,
      token: previousToken,
    };
  }

  environment.storage.setItem(SESSION_TOKEN_STORAGE_KEY, urlToken);

  if (previousToken !== urlToken) {
    clearStoredConvexSession(environment.storage);
  }

  currentUrl.searchParams.delete('token');
  environment.history.replaceState(null, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);

  return {
    replacedStoredToken: previousToken !== urlToken,
    scrubbedTokenFromUrl: true,
    token: urlToken,
  };
}

export function ensureConvexAuthBootstrap(
  client: ConvexClient,
  environment: BrowserEnvironment = getDefaultEnvironment(),
  options: EnsureConvexAuthBootstrapOptions = {},
): void {
  if (bootstrappedClients.has(client)) {
    return;
  }

  bootstrappedClients.add(client);
  const { onInviteTokenFailure } = options;
  let exchangeRequest: Promise<InviteTokenExchangeResponse> | null = null;
  let exchangeRequestInviteTokenHash: string | null = null;

  client.setAuth(
    async ({ forceRefreshToken }) => {
      const inviteToken = readStoredToken(environment.storage);

      if (!inviteToken) {
        clearStoredConvexSession(environment.storage);
        updateAuthDebugState({
          authenticated: false,
          inviteTokenSeen: false,
          lastError: null,
          lastInviteTokenHash: null,
          lastSessionExpiresAt: null,
          lastSessionIssuer: null,
          lastSessionSubject: null,
          sessionTokenReady: false,
        });
        return null;
      }

      const inviteTokenHash = await hashToken(inviteToken);
      const cachedSession = readStoredConvexSession(environment.storage);

      updateAuthDebugState({
        inviteTokenSeen: true,
        lastError: null,
        lastInviteTokenHash: inviteTokenHash,
      });

      if (
        !forceRefreshToken &&
        cachedSession &&
        cachedSession.inviteTokenHash === inviteTokenHash &&
        cachedSession.expiresAt > Date.now() + CONVEX_SESSION_REFRESH_LEEWAY_MS
      ) {
        updateAuthDebugState({
          lastSessionExpiresAt: cachedSession.expiresAt,
          sessionTokenReady: true,
        });
        return cachedSession.token;
      }

      if (!exchangeRequest || exchangeRequestInviteTokenHash !== inviteTokenHash || forceRefreshToken) {
        exchangeRequestInviteTokenHash = inviteTokenHash;
        exchangeRequest = exchangeInviteToken(inviteToken, environment).then((result) => {
          writeStoredConvexSession(environment.storage, {
            expiresAt: result.expiresAt,
            inviteTokenHash,
            token: result.token,
          });
          updateAuthDebugState({
            exchangeCount: getAuthDebugState().exchangeCount + 1,
            lastSessionExpiresAt: result.expiresAt,
            lastSessionIssuer: result.issuer,
            lastSessionSubject: result.identity.subject,
            sessionTokenReady: true,
          });
          return result;
        });
      }

      try {
        const result = await exchangeRequest;
        return result.token;
      } catch (error) {
        clearStoredToken(environment.storage);
        clearStoredConvexSession(environment.storage);
        const message = error instanceof Error ? error.message : 'Unknown Convex auth bootstrap error.';

        updateAuthDebugState({
          authenticated: false,
          lastError: message,
          lastSessionExpiresAt: null,
          lastSessionIssuer: null,
          lastSessionSubject: null,
          sessionTokenReady: false,
        });

        if (error instanceof InviteTokenExchangeFailure) {
          onInviteTokenFailure?.({
            message: error.message,
            reason: error.reason,
            status: error.status,
          });
          return null;
        }

        throw error;
      }
    },
    (isAuthenticated) => {
      updateAuthDebugState({
        authenticated: isAuthenticated,
      });
    },
  );
}
